<!-- ══════════════════════════════════════════════════════════════════════════
     report-viewer.component.html
     ══════════════════════════════════════════════════════════════════════════ -->

<!-- Loading state -->
<div *ngIf="!data" class="loading-state">
  Loading report…
</div>

<div *ngIf="data" class="report-shell">

  <!-- ── Toolbar ─────────────────────────────────────────────────────────── -->
  <div class="toolbar no-print">
    <span class="toolbar-title">Expense Reimbursement – Summary</span>
    <div class="toolbar-actions">
      <button class="btn btn-print" (click)="print()">🖨 Print</button>
      <button class="btn btn-pdf" (click)="downloadPdf()" [disabled]="generatingPdf">
        {{ generatingPdf ? '⏳ Generating…' : '⬇ Download PDF' }}
      </button>
    </div>
  </div>



  <!-- ── Pages ───────────────────────────────────────────────────────────── -->
  <div class="pages-wrapper" id="report-pages">

    <div *ngFor="let page of pages; trackBy: trackByPage" class="page">

      <!-- ── Logo ──────────────────────────────────────────────────────────── -->
      <div class="logo-row">
        <img [src]="companyLogo|imageUrl" class="align-middle m-2" alt="logo" />
      </div>
      <div *ngIf="data?.transaction?.isPosted === false" class="draft-watermark">
        DRAFT
      </div>

      <!-- ── Main form table ───────────────────────────────────────────────── -->
      <table class="form-table">

        <!-- TITLE BAR -->
        <tr class="title-row">
          <td colspan="6">Reimbursement Of Expenses - Summary</td>
        </tr>

        <!-- META ROW 1 : Date | Transaction No | CashBook | PartyType -->
        <tr class="meta-row">
          <td style="width:14%">
            DATE: {{ fmtDate(data.transaction?.transactionDate) }}
          </td>
          <td style="width:16%">
            Transaction No: {{ data.transaction?.transactionNo || '—' }}
          </td>
          <td style="width:26%" colspan="2">
            Cashbook: {{ data.transaction?.cashBookNameEn }}
          </td>
          <td style="width:44%" colspan="3">
            Party Type: {{ data.transaction?.partyTypeNameEn }}
          </td>
        </tr>

        <!-- META ROW 2 : Transaction type | Status | Comment | Posted -->
        <tr class="meta-row">
          <td colspan="6">
            Transaction Type: {{ data.transaction?.transactionTypeNameEn }}
            &nbsp;
          </td>

        </tr>


        <tr class="meta-row">
          <td colspan="6" style="padding:0; border:0">
            <table style="width:100%; table-layout:fixed; border:0; border-collapse:collapse;">
              <tr>
                <td class="col-border">
                  Employee Name: {{ data.transaction?.employeeNameEn }}
                </td>

                <td class="col-border">
                  Department Name: {{ data.transaction?.departmentNameEn }}
                </td>

                <td class="col-border-last">
                  Designation Name: {{ data.transaction?.designationNameEn }}
                </td>
              </tr>
            </table>
          </td>
        </tr>
        <!-- META ROW 3 : Description -->
        <tr class="meta-row">
          <td colspan="6">Description: {{ data.transaction?.descriptionEn }}</td>
        </tr>

        <!-- SPACER -->
        <tr class="spacer-row">
          <td colspan="6"></td>
        </tr>

        <!-- COLUMN HEADERS ROW 1 -->
        <tr class="col-header-row">
          <td rowspan="2" style="width:11%">Date</td>
          <td rowspan="2" style="width:10%">ReferenceNo #</td>
          <td rowspan="2" style="width:45%">Description</td>
        </tr>

        <!-- COLUMN HEADERS ROW 2 -->
        <tr class="col-header-row">
          <td style="width:12%">Amount</td>
          <td style="width:11%">Tax</td>
          <td style="width:11%">Net Amount</td>
        </tr>

        <!-- ── DATA ROWS ──────────────────────────────────────────────────── -->

        <ng-container *ngFor="let row of page.rows; trackBy: trackByRow">

          <!-- Tag header -->
          <tr *ngIf="row.type === 'tag'" class="tag-row">
            <td colspan="6">Tag: {{ row.tagName }} ({{ row.count }} items)</td>
          </tr>

          <!-- Detail -->
          <tr *ngIf="row.type === 'detail'" class="data-row" [class.alt]="row.index % 2 !== 0"
            [class.plain]="row.index % 2 === 0">
            <td class="text-center">{{ fmtDate(row.detail?.lineDate) }}</td>
            <td class="text-center">{{ row.detail?.referenceNo }}</td>
            <td>{{ row.detail?.descriptionEn }}</td>
            <td class="text-right">{{ fmtNumber(row.detail?.amount) }}</td>
            <td class="text-right">{{ fmtNumber(row.detail?.taxAmount) }}</td>
            <td class="text-right">{{ fmtNumber(row.detail?.netAmount) }}</td>
          </tr>

          <!-- Tag subtotal -->
          <tr *ngIf="row.type === 'subtotal'" class="subtotal-row">
            <td colspan="3" class="text-center">
              TOTAL – {{ row.group.tagName }} ({{ row.group.count }})
            </td>
            <td class="text-right">{{ fmtNumber(row.group.amount) }}</td>
            <td class="text-right">{{ fmtNumber(row.group.tax) }}</td>
            <td class="text-right">{{ fmtNumber(row.group.net) }}</td>
          </tr>
          <tr *ngIf="row.type === 'subtotal'" class="tag-gap">
            <td colspan="6"></td>
          </tr>

          <!-- Padding -->
          <tr *ngIf="row.type === 'empty'" class="data-row" [class.alt]="row.index % 2 !== 0"
            [class.plain]="row.index % 2 === 0">
            <td></td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
            <td class="text-right dash">-</td>
          </tr>

        </ng-container>

        <!-- ── TOTALS + FOOTER SECTION (last page only) ───────────────────── -->
        <ng-container *ngIf="page.isLast">

 
          
          <!-- Grand total -->
          <tr class="totals-row">
            <td colspan="3" class="text-center"> {{ hasTags ? 'Grand Total' : 'Total Expenses' }} In {{ currency }}</td>
            <td class="text-right">{{ fmtCurrency(totalAmount) }}</td>
            <td class="text-right">{{ fmtCurrency(totalTaxAmount) }}</td>
            <td class="text-right">{{ fmtCurrency(totalNet) }}</td>
          </tr>

          <!-- Spacer -->
          <tr class="spacer-row">
            <td colspan="6"></td>
          </tr>

          <!-- Amount in Words -->
          <tr class="words-row">
            <td colspan="6">
              <strong> Amount in Words:</strong>&nbsp;&nbsp;{{ totalInWords }}
            </td>
          </tr>

          <!-- Signature spacer -->
          <tr class="sig-spacer">
            <td colspan="6"></td>
          </tr>

          <!-- Signature headers -->
          <tr class="sig-header-row">
            <td colspan="3" class="text-center">Submitted BY</td>
            <td colspan="2" class="text-center">Status</td>
            <td colspan="3" class="text-center">Signature</td>
          </tr>

          <!-- Signature line 1 -->
          <tr *ngFor="let step of approvedHistory" class="sig-line-row">
            <td colspan="3" style="padding-inline-start:10px;">{{ step.userNameEn }}</td>
            <td colspan="2" class="text-center">{{ step.stepStatusNameEn || '-' }}</td>
            <td colspan="3" class="text-center signature-cell">
              <div class="signature-box" *ngIf="step.userSignature; else noSignature">
                <img [src]="getSignatureSrc(step.userSignature)" alt="signature" class="signature-img" />
              </div>
              <ng-template #noSignature>
                <span>-</span>
              </ng-template>
            </td>
          </tr>




          <!-- Date line -->
          <tr class="accounts-date">
            <td colspan="5"></td>
            <td colspan="2" class="text-right">Date : _______________</td>
          </tr>

        </ng-container>
        <!-- end last-page section -->

      </table>

      <!-- ── Page footer ───────────────────────────────────────────────────── -->
      <div class="page-footer">
        <span>{{ today }}</span>
        <span class="page-num">Page {{ page.pageNumber }} of {{ pages.length }}</span>
      </div>

    </div>
    <!-- end *ngFor page -->

  </div>
  <!-- end pages-wrapper -->

</div>
<!-- end report-shell -->